<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import { IBranch } from '@/interfaces/IBranch';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Sucursales» (plan §5.12, multi-branch stores): «Se ve en Centro y
   * Norte», opening a checklist of the active branches. Roles that do not
   * manage every branch can only change their own.
   */
  const props = withDefaults(
    defineProps<{
      /** The branches where it is hidden. */
      value: number[];
      /** The store's active branches. */
      branches: IBranch[];
      /** Only this branch can change (null = all). */
      onlyBranchId?: number | null;
      disabled?: boolean;
    }>(),
    { onlyBranchId: null, disabled: false }
  );

  const emit = defineEmits<{ (e: 'input', hiddenIds: number[]): void }>();

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const listId = nextDomId('branches-list');
  const open = ref(false);

  const shownNames = computed(() =>
    props.branches
      .filter((branch) => !props.value.includes(branch.id))
      .map((branch) => branch.name)
  );

  const summary = computed(() => {
    const names = shownNames.value;
    if (!names.length) return t('menuPage.editor.branches.none');
    if (names.length === props.branches.length)
      return t('menuPage.editor.branches.all');
    const last = names[names.length - 1];
    return names.length === 1
      ? t('menuPage.editor.branches.some', { names: last })
      : t('menuPage.editor.branches.some', {
          names: t('menuPage.editor.branches.andList', {
            first: names.slice(0, -1).join(', '),
            last,
          }),
        });
  });

  function canChange(branch: IBranch): boolean {
    return (
      !props.disabled &&
      (props.onlyBranchId === null || props.onlyBranchId === branch.id)
    );
  }

  function toggle(branch: IBranch) {
    if (!canChange(branch)) return;
    const hidden = props.value.includes(branch.id)
      ? props.value.filter((id) => id !== branch.id)
      : [...props.value, branch.id].sort((a, b) => a - b);
    emit('input', hidden);
  }
</script>

<template>
  <div class="v2-catalog-branches-field">
    <button
      type="button"
      class="branches-summary"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="listId"
      @click="open = !open"
    >
      <span class="summary-label">{{
        t('menuPage.editor.branches.label')
      }}</span>
      <span class="summary-value">{{ summary }}</span>
      <i
        class="mdi mdi-chevron-down summary-chevron"
        :class="{ 'is-open': open }"
      />
    </button>
    <div v-show="open" :id="listId" class="branches-list">
      <label
        v-for="branch in branches"
        :key="branch.id"
        class="branch-row"
        :class="{ 'is-locked': !canChange(branch) }"
      >
        <input
          type="checkbox"
          class="branch-check"
          :checked="!value.includes(branch.id)"
          :disabled="!canChange(branch)"
          @change="toggle(branch)"
        />
        <span class="branch-name">{{ branch.name }}</span>
      </label>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-branches-field {
    .branches-summary {
      @apply tw-flex tw-min-h-[48px] tw-w-full tw-cursor-pointer tw-items-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-0 tw-text-left;

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      .summary-label {
        @apply tw-mr-3 tw-flex-none tw-text-sm tw-font-medium tw-text-neutral-900;
      }

      .summary-value {
        @apply tw-min-w-0 tw-flex-1 tw-truncate tw-text-right tw-text-sm tw-text-neutral-600;
      }

      .summary-chevron {
        @apply tw-ml-1 tw-flex-none tw-text-xl tw-leading-none tw-text-neutral-500 tw-transition-transform tw-duration-150;

        &.is-open {
          @apply tw-rotate-180;
        }
      }
    }

    .branches-list {
      @apply tw-mb-1 tw-rounded-lg tw-bg-canvas tw-px-3 tw-py-1;
    }

    .branch-row {
      @apply tw-flex tw-min-h-[44px] tw-cursor-pointer tw-items-center tw-text-sm tw-text-neutral-800;

      &.is-locked {
        @apply tw-cursor-default tw-text-neutral-400;
      }

      .branch-check {
        @apply tw-mr-3 tw-h-5 tw-w-5 tw-flex-none;
        accent-color: rgb(var(--c-neutral-900));
      }

      .branch-name {
        @apply tw-min-w-0 tw-truncate;
      }
    }
  }
</style>
